import React, { useEffect, useState, createContext, useContext } from 'react';
export interface TimeSlotConfig {
  id: string;
  time: string;
  capacity: number;
  modality: 'presencial' | 'virtual';
  price?: number;
  active: boolean;
}
export interface AvailabilityData {
  [dateKey: string]: TimeSlotConfig[];
}
interface AvailabilityContextType {
  availability: AvailabilityData;
  getAvailableDates: (modality?: 'presencial' | 'virtual') => Set<string>;
  getSlotsForDate: (dateKey: string, modality?: 'presencial' | 'virtual') => TimeSlotConfig[];
  addSlot: (dateKey: string, slot: Omit<TimeSlotConfig, 'id' | 'active'>) => void;
  removeSlot: (dateKey: string, slotId: string) => void;
  toggleSlotActive: (dateKey: string, slotId: string) => void;
}
const AvailabilityContext = createContext<AvailabilityContextType | undefined>(undefined);
const DEFAULT_AVAILABILITY: AvailabilityData = {};
export function AvailabilityProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [availability, setAvailability] = useState<AvailabilityData>(() => {
    const saved = localStorage.getItem('vimutti_availability');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Migrate old slots that don't have 'active' field
        Object.keys(parsed).forEach(dateKey => {
          parsed[dateKey] = parsed[dateKey].map((slot: any) => ({
            ...slot,
            active: slot.active !== undefined ? slot.active : true
          }));
        });
        return parsed;
      } catch (e) {
        return DEFAULT_AVAILABILITY;
      }
    }
    return DEFAULT_AVAILABILITY;
  });
  useEffect(() => {
    localStorage.setItem('vimutti_availability', JSON.stringify(availability));
  }, [availability]);
  // Public-facing: only returns dates/slots where at least one slot is active
  const getAvailableDates = (modality?: 'presencial' | 'virtual') => {
    const dates = new Set<string>();
    Object.entries(availability).forEach(([dateKey, slots]) => {
      const activeSlots = slots.filter(s => s.active);
      if (activeSlots.length > 0) {
        if (!modality || activeSlots.some(s => s.modality === modality)) {
          dates.add(dateKey);
        }
      }
    });
    return dates;
  };
  // Public-facing: only returns active slots
  const getSlotsForDate = (dateKey: string, modality?: 'presencial' | 'virtual') => {
    const slots = (availability[dateKey] || []).filter(s => s.active);
    if (modality) {
      return slots.filter(s => s.modality === modality);
    }
    return slots;
  };
  const addSlot = (dateKey: string, slot: Omit<TimeSlotConfig, 'id' | 'active'>) => {
    setAvailability(prev => {
      const daySlots = prev[dateKey] || [];
      const newSlot: TimeSlotConfig = {
        ...slot,
        id: Math.random().toString(36).substring(2, 9),
        active: true
      };
      return {
        ...prev,
        [dateKey]: [...daySlots, newSlot]
      };
    });
  };
  const removeSlot = (dateKey: string, slotId: string) => {
    setAvailability(prev => {
      const daySlots = prev[dateKey] || [];
      const filtered = daySlots.filter(s => s.id !== slotId);
      const newData = {
        ...prev
      };
      if (filtered.length === 0) {
        delete newData[dateKey];
      } else {
        newData[dateKey] = filtered;
      }
      return newData;
    });
  };
  const toggleSlotActive = (dateKey: string, slotId: string) => {
    setAvailability(prev => {
      const daySlots = prev[dateKey] || [];
      return {
        ...prev,
        [dateKey]: daySlots.map(s => s.id === slotId ? {
          ...s,
          active: !s.active
        } : s)
      };
    });
  };
  return <AvailabilityContext.Provider value={{
    availability,
    getAvailableDates,
    getSlotsForDate,
    addSlot,
    removeSlot,
    toggleSlotActive
  }} data-id="element-1169">
      {children}
    </AvailabilityContext.Provider>;
}
export function useAvailability() {
  const context = useContext(AvailabilityContext);
  if (context === undefined) {
    throw new Error('useAvailability must be used within an AvailabilityProvider');
  }
  return context;
}